import { supabase } from '../lib/supabaseClient';
import { KalmanFilter } from './KalmanFilter';

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: string;
}

interface BackgroundLocationState {
  isTracking: boolean;
  sharingId: string;
  userId: string;
  username: string;
  userGroup: string;
  deviceId: string;
  lastLocation: LocationData | null;
  lastUpdateTime: number;
}

class BackgroundLocationService {
  private static instance: BackgroundLocationService;
  private state: BackgroundLocationState = {
    isTracking: false,
    sharingId: '',
    userId: '',
    username: '',
    userGroup: '',
    deviceId: '',
    lastLocation: null,
    lastUpdateTime: 0
  };

  private updateInterval: NodeJS.Timeout | null = null;
  private kalmanFilter: KalmanFilter | null = null;
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
  private isPageVisible = true;
  private isInitialized = false;

  private constructor() {
    this.initialize();
  }

  static getInstance(): BackgroundLocationService {
    if (!BackgroundLocationService.instance) {
      BackgroundLocationService.instance = new BackgroundLocationService();
    }
    return BackgroundLocationService.instance;
  }

  private async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize service worker first
      await this.initializeServiceWorker();
      
      // Load saved state
      this.loadSavedState();
      
      // Set up page visibility listener
      this.setupPageVisibilityListener();
      
      // Set up beforeunload listener
      this.setupBeforeUnloadListener();
      
      // Set up periodic state persistence
      this.setupPeriodicStatePersistence();
      
      // Set up heartbeat to keep service worker alive
      this.setupHeartbeat();
      
      this.isInitialized = true;
      console.log('BackgroundLocationService: Initialized successfully');
    } catch (error) {
      console.error('BackgroundLocationService: Initialization failed:', error);
    }
  }

  private async initializeServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('BackgroundLocationService: Service worker not supported');
      return;
    }

    try {
      this.serviceWorkerRegistration = await navigator.serviceWorker.register('/background-location-worker.js');
      console.log('BackgroundLocationService: Service worker registered');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('BackgroundLocationService: Service worker ready');

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('BackgroundLocationService: Received message from service worker:', event.data);
        
        if (event.data.type === 'LOCATION_UPDATE') {
          this.handleLocationUpdateFromServiceWorker(event.data.location);
        } else if (event.data.type === 'HEARTBEAT') {
          console.log('BackgroundLocationService: Service worker heartbeat received');
        }
      });

    } catch (error) {
      console.error('BackgroundLocationService: Service worker registration failed:', error);
    }
  }

  private setupPageVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      this.isPageVisible = !document.hidden;
      console.log('BackgroundLocationService: Page visibility changed:', this.isPageVisible);
      
      if (this.isPageVisible && this.state.isTracking) {
        // Page became visible, ensure tracking is active
        this.ensureTrackingActive();
      }
    });

    // Listen for window focus/blur events
    window.addEventListener('focus', () => {
      console.log('BackgroundLocationService: Window focused');
      if (this.state.isTracking) {
        this.ensureTrackingActive();
      }
    });

    window.addEventListener('blur', () => {
      console.log('BackgroundLocationService: Window blurred');
      // Don't stop tracking, just log the event
    });
  }

  private setupBeforeUnloadListener(): void {
    window.addEventListener('beforeunload', () => {
      if (this.state.isTracking) {
        console.log('BackgroundLocationService: Page unloading, saving state and notifying service worker');
        this.saveTrackingState();
        
        // Notify service worker to continue tracking
        if (this.serviceWorkerRegistration && this.serviceWorkerRegistration.active) {
          this.serviceWorkerRegistration.active.postMessage({
            type: 'CONTINUE_TRACKING',
            data: {
              isTracking: true,
              sharingId: this.state.sharingId,
              userId: this.state.userId,
              username: this.state.username,
              userGroup: this.state.userGroup,
              deviceId: this.state.deviceId
            }
          });
        }
      }
    });
  }

  private setupPeriodicStatePersistence(): void {
    // Save state every 15 seconds to ensure persistence
    setInterval(() => {
      if (this.state.isTracking) {
        this.saveTrackingState();
      }
    }, 15000);
  }

  private setupHeartbeat(): void {
    // Send heartbeat to service worker every 10 seconds to keep it alive
    setInterval(() => {
      if (this.state.isTracking && this.serviceWorkerRegistration && this.serviceWorkerRegistration.active) {
        this.serviceWorkerRegistration.active.postMessage({
          type: 'HEARTBEAT',
          data: {
            timestamp: Date.now(),
            isTracking: this.state.isTracking
          }
        });
      }
    }, 10000);
  }

  private saveTrackingState(): void {
    if (this.state.isTracking) {
      const stateData = {
        isTracking: true,
        sharingId: this.state.sharingId,
        userId: this.state.userId,
        username: this.state.username,
        userGroup: this.state.userGroup,
        deviceId: this.state.deviceId,
        timestamp: Date.now()
      };
      
      localStorage.setItem('background_location_state', JSON.stringify(stateData));
      console.log('BackgroundLocationService: Saved tracking state:', stateData);
    }
  }

  private loadSavedState(): boolean {
    try {
      const savedState = localStorage.getItem('background_location_state');
      if (savedState) {
        const state = JSON.parse(savedState);
        const timeSinceSaved = Date.now() - state.timestamp;
        
        // Only restore if saved within last 15 minutes
        if (timeSinceSaved < 15 * 60 * 1000) {
          this.state = {
            ...this.state,
            isTracking: state.isTracking,
            sharingId: state.sharingId,
            userId: state.userId,
            username: state.username,
            userGroup: state.userGroup,
            deviceId: state.deviceId,
            lastUpdateTime: 0
          };
          
          console.log('BackgroundLocationService: Restored tracking state:', state);
          return true;
        } else {
          // Clear old state
          localStorage.removeItem('background_location_state');
          console.log('BackgroundLocationService: Cleared old tracking state');
        }
      }
    } catch (error) {
      console.error('BackgroundLocationService: Error loading tracking state:', error);
    }
    return false;
  }

  async startTracking(
    sharingId: string,
    userId: string,
    username: string,
    userGroup: string
  ): Promise<boolean> {
    try {
      console.log('BackgroundLocationService: Starting background tracking:', {
        sharingId,
        userId,
        username,
        userGroup
      });

      // Get or create device ID
      let deviceId = localStorage.getItem('onemove_device_id');
      if (!deviceId) {
        const browserFingerprint = `${navigator.userAgent}-${navigator.language}-${screen.width}x${screen.height}`;
        const fingerprintHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(browserFingerprint))
          .then(hash => Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 8));
        deviceId = `web-${fingerprintHash}-${crypto.randomUUID().substring(0, 8)}`;
        localStorage.setItem('onemove_device_id', deviceId);
      }

      // Initialize Kalman filter
      this.kalmanFilter = KalmanFilter.getInstance();

      // Update state
      this.state = {
        isTracking: true,
        sharingId,
        userId,
        username,
        userGroup,
        deviceId,
        lastLocation: null,
        lastUpdateTime: 0
      };

      // Save state
      this.saveTrackingState();

      // Start periodic updates
      this.startPeriodicUpdates();

      // Notify service worker to start tracking
      if (this.serviceWorkerRegistration && this.serviceWorkerRegistration.active) {
        this.serviceWorkerRegistration.active.postMessage({
          type: 'START_TRACKING',
          data: {
            sharingId,
            userId,
            username,
            userGroup,
            deviceId
          }
        });
      }

      console.log('BackgroundLocationService: Background tracking started successfully');
      console.log('BackgroundLocationService: Location tracking will continue even when page is minimized or navigated away');
      return true;

    } catch (error) {
      console.error('BackgroundLocationService: Error starting tracking:', error);
      return false;
    }
  }

  async stopTracking(): Promise<void> {
    console.log('BackgroundLocationService: Stopping background tracking');

    // Clear state
    this.state.isTracking = false;
    this.state.lastLocation = null;
    this.state.lastUpdateTime = 0;

    // Clear timers
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    // Clear saved state
    localStorage.removeItem('background_location_state');

    // Notify service worker to stop tracking
    if (this.serviceWorkerRegistration && this.serviceWorkerRegistration.active) {
      this.serviceWorkerRegistration.active.postMessage({
        type: 'STOP_TRACKING'
      });
    }

    // Remove location from database
    if (this.state.userId && this.state.deviceId) {
      try {
        await supabase
          .from('shared_location')
          .delete()
          .eq('user_id', this.state.userId)
          .eq('platform', 'web')
          .eq('device_id', this.state.deviceId);
        
        console.log('BackgroundLocationService: Removed location from database');
      } catch (error) {
        console.error('BackgroundLocationService: Error removing location from database:', error);
      }
    }

    console.log('BackgroundLocationService: Background tracking stopped');
  }

  private startPeriodicUpdates(): void {
    // Clear any existing interval
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    // Start periodic updates every 15 seconds
    this.updateInterval = setInterval(async () => {
      if (this.state.isTracking) {
        try {
          console.log('BackgroundLocationService: Executing periodic location update');
          await this.updateLocation();
        } catch (error) {
          console.error('BackgroundLocationService: Error in periodic update:', error);
        }
      } else {
        // Stop interval if tracking is disabled
        if (this.updateInterval) {
          clearInterval(this.updateInterval);
          this.updateInterval = null;
        }
      }
    }, 15000); // 15 seconds

    console.log('BackgroundLocationService: Periodic updates started (15s interval)');
  }

  private async updateLocation(): Promise<void> {
    if (!navigator.geolocation) {
      console.error('BackgroundLocationService: Geolocation not supported');
      return;
    }

    try {
      const position = await this.getCurrentPosition();
      await this.handleLocationUpdate(position);
    } catch (error) {
      console.error('BackgroundLocationService: Error updating location:', error);
    }
  }

  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    });
  }

  private async handleLocationUpdate(position: GeolocationPosition): Promise<void> {
    if (!this.state.isTracking) {
      return;
    }

    const { latitude, longitude, accuracy } = position.coords;
    const timestamp = new Date().toISOString();

    // Apply Kalman filter for location smoothing
    const filteredLocation = this.kalmanFilter ? 
      this.kalmanFilter.applyFilter({
        latitude,
        longitude,
        accuracy,
        timestamp: Date.now()
      }) : 
      { latitude, longitude, accuracy };

    // Check if we should update (movement > 2m or time > 2s)
    const shouldUpdate = this.shouldUpdateLocation(filteredLocation);
    if (!shouldUpdate) {
      console.log('BackgroundLocationService: Skipping update (insufficient movement/time)');
      return;
    }

    // Update last location
    this.state.lastLocation = {
      latitude: filteredLocation.latitude,
      longitude: filteredLocation.longitude,
      accuracy: filteredLocation.accuracy,
      timestamp
    };
    this.state.lastUpdateTime = Date.now();

    // Update Supabase immediately
    await this.updateLocationInDatabase(filteredLocation, timestamp);

    console.log('BackgroundLocationService: Location updated:', {
      latitude: filteredLocation.latitude,
      longitude: filteredLocation.longitude,
      accuracy: filteredLocation.accuracy,
      timestamp
    });
  }

  private handleLocationUpdateFromServiceWorker(location: any): void {
    if (!this.state.isTracking) {
      return;
    }

    console.log('BackgroundLocationService: Received location update from service worker:', location);
    
    // Update Supabase with location from service worker
    this.updateLocationInDatabase({
      latitude: location.latitude,
      longitude: location.longitude
    }, location.timestamp);
  }

  private shouldUpdateLocation(location: { latitude: number; longitude: number }): boolean {
    if (!this.state.lastLocation) {
      return true;
    }

    const timeDiff = Date.now() - this.state.lastUpdateTime;
    const distance = Math.sqrt(
      Math.pow(location.latitude - this.state.lastLocation.latitude, 2) +
      Math.pow(location.longitude - this.state.lastLocation.longitude, 2)
    ) * 111000; // Convert to meters

    return distance > 2 || timeDiff > 2000; // 2m or 2s
  }

  private async updateLocationInDatabase(location: { latitude: number; longitude: number }, timestamp: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('shared_location')
        .upsert({
          user_id: this.state.userId,
          sharing_id: this.state.sharingId,
          username: this.state.username,
          user_group: this.state.userGroup,
          shared_location: 'Getting address...', // Will update later
          platform: 'web',
          device_id: this.state.deviceId,
          eta: 'Calculating...', // Will update later
          latitude: location.latitude,
          longitude: location.longitude,
          last_updated: timestamp
        }, {
          onConflict: 'user_id,platform,device_id'
        });

      if (error) {
        console.error('BackgroundLocationService: Error updating location in database:', error);
      } else {
        // Trigger real-time update event
        window.dispatchEvent(new CustomEvent('locationUpdate', {
          detail: {
            type: 'INSERT',
            data: {
              user_id: this.state.userId,
              sharing_id: this.state.sharingId,
              platform: 'web',
              device_id: this.state.deviceId,
              latitude: location.latitude,
              longitude: location.longitude,
              username: this.state.username,
              user_group: this.state.userGroup,
              last_updated: timestamp
            }
          }
        }));
      }
    } catch (error) {
      console.error('BackgroundLocationService: Error in updateLocationInDatabase:', error);
    }
  }

  private ensureTrackingActive(): void {
    if (this.state.isTracking && !this.updateInterval) {
      console.log('BackgroundLocationService: Restarting periodic updates');
      this.startPeriodicUpdates();
    }
  }

  // Public methods for external access
  isTracking(): boolean {
    return this.state.isTracking;
  }

  getTrackingState(): BackgroundLocationState {
    return { ...this.state };
  }

  // Restore tracking on page load
  async restoreTracking(): Promise<boolean> {
    if (this.loadSavedState() && this.state.isTracking) {
      console.log('BackgroundLocationService: Restoring background tracking');
      
      // Initialize Kalman filter
      this.kalmanFilter = KalmanFilter.getInstance();
      
      // Start periodic updates
      this.startPeriodicUpdates();
      
      // Notify service worker to continue tracking
      if (this.serviceWorkerRegistration && this.serviceWorkerRegistration.active) {
        this.serviceWorkerRegistration.active.postMessage({
          type: 'CONTINUE_TRACKING',
          data: {
            isTracking: true,
            sharingId: this.state.sharingId,
            userId: this.state.userId,
            username: this.state.username,
            userGroup: this.state.userGroup,
            deviceId: this.state.deviceId
          }
        });
      }
      
      return true;
    }
    return false;
  }
}

export default BackgroundLocationService; 